import React from 'react';
import { Link } from 'react-router-dom';

/* ── Data ────────────────────────────────────────────────────────── */

const HERO_STATS = [
  { value: '96%',  label: 'of enterprise codebases\nare open source',         source: 'Synopsys OSSRA 2024' },
  { value: '700%', label: 'increase in repository\nattacks over 3 years',     source: 'Sonatype 2024' },
  { value: '\u20AC15M',  label: 'maximum EU CRA penalty\nper violation',       source: 'EU CRA Article 64' },
  { value: '11%',  label: 'of annual revenue lost\nin supply chain breaches',  source: 'SolarWinds Research' },
];

const PILLARS = [
  { num: '01', icon: '\u{1F6E1}\uFE0F', color: '#ef4444', bg: 'rgba(239,68,68,.07)',    name: 'Vulnerability Intelligence', desc: 'Real-time CVE and GHSA scanning via OSV.dev across every package in every SBOM. CVSS vectors, severity tiers, and exploitability context surface in seconds.' },
  { num: '02', icon: '\u2696\uFE0F',    color: '#f59e0b', bg: 'rgba(245,158,11,.07)',   name: 'License Compliance',         desc: 'Risk-categorized license detection across permissive, weak copyleft, strong copyleft, and restricted categories. Catches GPL contamination, LGPL boundary violations, and blocked licenses before they reach production.' },
  { num: '03', icon: '\u{1F49A}',       color: '#10b981', bg: 'rgba(16,185,129,.07)',   name: 'Package Health',             desc: 'OpenSSF Scorecard integration with abandoned package detection, commit velocity tracking, and single-maintainer risk analysis. Flags critical dependencies that are becoming single points of failure.' },
  { num: '04', icon: '\u{1F4CA}',       color: '#6366f1', bg: 'rgba(99,102,241,.07)',   name: 'SBOM Quality',               desc: 'Five-dimension quality scoring: completeness, NTIA minimum elements, PURL coverage, dependency relationships, and supplier data. Graded A to F, with automatic rejection of low-quality SBOMs at the CI/CD gate.' },
  { num: '05', icon: '\u{1F4CB}',       color: '#8b5cf6', bg: 'rgba(139,92,246,.07)',   name: 'Regulatory Compliance',      desc: 'Automated EU CRA audit reports per SBOM with NIST SP 800-53 and ISO 27001 control mapping. One-click compliance documentation for regulators, customers, and internal governance.' },
];

const COMPARE = [
  { feature: 'Time to first insight',    dg: '< 5 minutes',           health: '3+ months',     sca: 'Days',         diy: 'Months' },
  { feature: 'Input format',             dg: 'SPDX + CycloneDX SBOM', health: 'PURLs only',    sca: 'Package list', diy: 'Custom' },
  { feature: 'Analysis speed',           dg: '< 60 seconds',          health: '2\u20133 hours', sca: 'Minutes',     diy: 'Hours' },
  { feature: 'Vulnerability scanning',   dg: 'yes',    health: 'no',      sca: 'yes',     diy: 'yes' },
  { feature: 'License risk',             dg: 'yes',    health: 'no',      sca: 'partial', diy: 'yes' },
  { feature: 'Package health',           dg: 'yes',    health: 'yes',     sca: 'no',      diy: 'yes' },
  { feature: 'SBOM quality scoring',     dg: 'yes',    health: 'no',      sca: 'no',      diy: 'yes' },
  { feature: 'EU CRA audit report',      dg: 'yes',    health: 'no',      sca: 'no',      diy: 'partial' },
  { feature: 'Policy CI/CD gates',       dg: 'yes',    health: 'partial', sca: 'partial', diy: 'yes' },
  { feature: 'AI-powered triage',        dg: 'yes',    health: 'no',      sca: 'no',      diy: 'no' },
  { feature: 'Proprietary vuln research', dg: 'no',     health: 'no',      sca: 'yes',     diy: 'no' },
  { feature: 'Deep language SAST',       dg: 'no',     health: 'no',      sca: 'yes',     diy: 'partial' },
  { feature: 'Cross-project impact map', dg: 'yes',    health: 'no',      sca: 'no',      diy: 'no' },
  { feature: 'Deployment model',         dg: 'SaaS / self-hosted', health: 'Consulting only', sca: 'Cloud only', diy: 'Any' },
];

const WORKFLOW = [
  { n: '01', icon: '\u{1F4E4}', title: 'Upload SBOM',        desc: 'Drag-and-drop SPDX or CycloneDX JSON. Or auto-generate from source using Syft. Supports manual upload, API push, and automated CI/CD pipeline submission.' },
  { n: '02', icon: '\u26A1',     title: 'Instant Analysis',   desc: 'All five dimensions computed in parallel: CVE scan, license risk, package health, SBOM quality, compliance gaps. Complete in under 60 seconds.' },
  { n: '03', icon: '\u{1F4CA}',  title: 'Unified Dashboard',  desc: 'A single composite risk score with drill-down into every dimension. Trend analysis over time. Cross-project impact graph showing which projects share affected packages.' },
  { n: '04', icon: '\u{1F512}',  title: 'Policy Gates',       desc: 'Automated CI/CD integration blocks builds that violate your policy. Native support for GitHub Actions and Azure DevOps, with CLI compatibility for other runners.' },
  { n: '05', icon: '\u{1F3AF}',  title: 'Remediate & Track',  desc: 'Assign owners, set due dates, track remediation progress. VEX statements document accepted risks with justification. Full audit trail for every decision.' },
  { n: '06', icon: '\u{1F4CB}',  title: 'Compliance Report',  desc: 'Generate EU CRA audit documents with one click. Export for regulators, enterprise procurement, or internal governance. Downloadable PDF, shareable link.' },
];

const CRA = [
  { art: 'Art. 13',  req: 'Vulnerability handling obligations',       dg: 'Real-time CVE monitoring via OSV.dev and GHSA, with remediation tracking' },
  { art: 'Art. 14',  req: 'Vulnerability reporting to authorities',   dg: 'VEX statements with CycloneDX export + automated webhook alerts' },
  { art: 'Art. 20',  req: 'Technical documentation: SBOM required', dg: 'Full SBOM upload, quality scoring (A\u2013F), NTIA compliance validation' },
  { art: 'Art. 13(6)', req: 'Due diligence on third-party components',  dg: 'Package health + OpenSSF Scorecard + abandoned package detection' },
  { art: 'Annex I',  req: 'Security-by-design requirements',           dg: 'Policy engine + CI/CD gates + SBOM quality thresholds' },
  { art: 'Annex II', req: 'User information and documentation',        dg: 'One-click EU CRA audit report generation per SBOM' },
];

const ROI_CARDS = [
  {
    icon: '\u{1F6E1}\uFE0F', color: '#ef4444', title: 'Risk Reduction',
    points: [
      'Detect critical CVEs before they reach production',
      'Eliminate abandoned dependencies, the #1 supply chain attack vector',
      'Automatically block GPL contamination in proprietary codebases',
      'VEX statements document accepted risks for auditors and regulators',
    ],
  },
  {
    icon: '\u26A1', color: '#f59e0b', title: 'Operational Efficiency',
    points: [
      '60-second analysis replaces hours of multi-tool processing',
      'Single dashboard replaces 4\u20136 disconnected point-tool interfaces',
      'Automated CI/CD gates eliminate manual security review bottlenecks',
      'AI-powered triage prioritizes the vulnerabilities that actually matter',
    ],
  },
  {
    icon: '\u{1F4CB}', color: '#10b981', title: 'Regulatory Readiness',
    points: [
      'EU CRA audit documents generated in one click per SBOM',
      'NIST SP 800-53 and ISO 27001 control mapping included',
      'SBOM quality gates ensure Article 20 documentation compliance',
      'Immutable audit trail for every vulnerability management decision',
    ],
  },
];

/* ── Helpers ─────────────────────────────────────────────────────── */

function cellVal(v) {
  if (v === 'yes')     return { label: '\u2713 Yes',     color: '#10b981' };
  if (v === 'no')      return { label: '\u2717 No',      color: '#ef4444' };
  if (v === 'partial') return { label: '\u223C Partial', color: '#f59e0b' };
  return { label: v, color: '#64748b' };
}

function SectionDivider({ num, title }) {
  return (
    <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:40,paddingTop:56,borderTop:'2px solid #f1f5f9'}}>
      <span style={{
        fontSize:11,fontWeight:900,color:'#6366f1',letterSpacing:'.8px',textTransform:'uppercase',
        border:'2px solid #6366f1',borderRadius:6,padding:'4px 10px',flexShrink:0,
      }}>{num}</span>
      <h2 style={{fontSize:'clamp(22px,3vw,30px)',fontWeight:800,color:'#1e293b',margin:0,letterSpacing:'-.5px',lineHeight:1.1}}>{title}</h2>
    </div>
  );
}

function PullQuote({ children, source }) {
  return (
    <blockquote style={{
      borderLeft:'4px solid #6366f1',
      margin:'40px 0',
      padding:'24px 28px',
      background:'rgba(99,102,241,.04)',
      borderRadius:'0 14px 14px 0',
    }}>
      <p style={{fontSize:18,color:'#1e293b',fontStyle:'italic',lineHeight:1.75,margin:0,fontWeight:500}}>{children}</p>
      {source && <footer style={{fontSize:13,color:'#6366f1',fontStyle:'normal',fontWeight:600,marginTop:10,display:'block'}}>{'\u2014 '}{source}</footer>}
    </blockquote>
  );
}

/* ── Main component ──────────────────────────────────────────────── */

export default function WebsiteWhitepaperPage() {
  return (
    <div className="dgw-page-wrap" style={{fontFamily:"'Inter', system-ui, sans-serif"}}>

      {/* Hero */}
      <section style={{
        background:'linear-gradient(160deg, #04080f 0%, #080d1c 60%, #0c1528 100%)',
        padding:'100px clamp(20px,5vw,60px) 80px',
        position:'relative',overflow:'hidden',
      }}>
        <div style={{position:'absolute',top:'-120px',right:'-60px',width:700,height:700,borderRadius:'50%',background:'radial-gradient(circle, rgba(99,102,241,.12), transparent 70%)',pointerEvents:'none'}} />
        <div style={{position:'absolute',bottom:'-60px',left:'5%',width:500,height:500,borderRadius:'50%',background:'radial-gradient(circle, rgba(139,92,246,.07), transparent 70%)',pointerEvents:'none'}} />

        <div style={{maxWidth:960,margin:'0 auto',position:'relative',zIndex:1}}>
          <div style={{display:'inline-flex',alignItems:'center',gap:8,background:'rgba(99,102,241,.15)',border:'1px solid rgba(99,102,241,.3)',borderRadius:100,padding:'7px 18px',marginBottom:36}}>
            <span style={{width:7,height:7,borderRadius:'50%',background:'#818cf8',display:'block'}} />
            <span style={{fontSize:11,fontWeight:700,letterSpacing:'1px',textTransform:'uppercase',color:'#a5b4fc'}}>White Paper</span>
            <span style={{color:'rgba(255,255,255,.2)'}}>|</span>
            <span style={{fontSize:11,color:'#818cf8',fontWeight:600}}>DepGuard by Lexint</span>
          </div>

          <h1 style={{fontSize:'clamp(30px,5.5vw,62px)',fontWeight:900,color:'#f1f5f9',letterSpacing:'-2px',lineHeight:1.06,marginBottom:8,maxWidth:780}}>
            Beyond Vulnerability Scanning:
          </h1>
          <h1 style={{
            fontSize:'clamp(30px,5.5vw,62px)',fontWeight:900,letterSpacing:'-2px',lineHeight:1.06,marginBottom:28,
            background:'linear-gradient(90deg, #818cf8 0%, #a78bfa 50%, #67e8f9 100%)',
            WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',
          }}>
            The Case for Complete SBOM Intelligence
          </h1>

          <p style={{fontSize:18,color:'#94a3b8',lineHeight:1.75,maxWidth:640,marginBottom:56}}>
            How organizations with tens of thousands of repositories achieve real-time visibility into
            vulnerability, license, health, quality, and regulatory compliance, from a single SBOM upload,
            in under 60 seconds.
          </p>

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))',background:'rgba(255,255,255,.05)',border:'1px solid rgba(255,255,255,.07)',borderRadius:18,overflow:'hidden'}}>
            {HERO_STATS.map((s, i) => (
              <div key={s.value} style={{
                padding:'28px 20px',textAlign:'center',
                borderRight: i < HERO_STATS.length - 1 ? '1px solid rgba(255,255,255,.06)' : 'none',
              }}>
                <div style={{fontSize:'clamp(28px,4vw,44px)',fontWeight:900,color:'#f1f5f9',letterSpacing:'-1px',lineHeight:1}}>{s.value}</div>
                <div style={{fontSize:12,color:'#64748b',marginTop:8,lineHeight:1.45,whiteSpace:'pre-line'}}>{s.label}</div>
                <div style={{fontSize:10,color:'#475569',marginTop:5,fontStyle:'italic'}}>{s.source}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Document body */}
      <div style={{maxWidth:920,margin:'0 auto',padding:'72px clamp(20px,5vw,48px) 80px'}}>

        {/* Table of Contents */}
        <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:16,padding:'32px 40px',marginBottom:80}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:'.8px',textTransform:'uppercase',color:'#94a3b8',marginBottom:20}}>Table of Contents</div>
          {[
            ['I',   'The Open Source Risk Problem'],
            ['II',  'Why Point Solutions Fail at Scale'],
            ['III', 'Five Dimensions of SBOM Intelligence'],
            ['IV',  'How DepGuard Works: From Upload to Compliance'],
            ['V',   'EU CRA Compliance: Built In, Not Bolted On'],
            ['VI',  'Enterprise at Scale: Architecture & Performance'],
            ['VII', 'Business Impact & Return on Investment'],
          ].map(([num, title], i, arr) => (
            <div key={num} style={{
              display:'flex',alignItems:'center',gap:20,padding:'10px 0',
              borderBottom: i < arr.length - 1 ? '1px solid #f1f5f9' : 'none',
            }}>
              <span style={{fontSize:12,fontWeight:800,color:'#6366f1',width:24,flexShrink:0}}>{num}</span>
              <span style={{fontSize:15,color:'#334155',lineHeight:1.3}}>{title}</span>
            </div>
          ))}
        </div>

        {/* Executive Summary */}
        <div style={{marginBottom:64}}>
          <div style={{
            display:'inline-block',fontSize:10,fontWeight:800,letterSpacing:'1px',textTransform:'uppercase',
            color:'#6366f1',background:'rgba(99,102,241,.08)',border:'1px solid rgba(99,102,241,.2)',
            borderRadius:6,padding:'4px 10px',marginBottom:20,
          }}>Executive Summary</div>
          <p style={{fontSize:18,color:'#1e293b',lineHeight:1.85,marginBottom:20,fontWeight:500}}>
            The open source software supply chain is under unprecedented pressure. Security attacks are escalating,
            regulations are tightening, and the complexity of managing hundreds, or hundreds of thousands, of
            open source dependencies grows with every release cycle.
          </p>
          <p style={{fontSize:17,color:'#475569',lineHeight:1.85,marginBottom:20}}>
            Most organizations respond with a fragmented collection of point tools: a CVE scanner here, a license
            checker there, a health monitor elsewhere. This approach leaves dangerous blind spots, creates operational
            overhead, and fails entirely when regulators request EU CRA compliance documentation.
          </p>
          <p style={{fontSize:17,color:'#475569',lineHeight:1.85}}>
            This whitepaper makes the case for a unified approach. One platform that delivers complete visibility
            across five critical dimensions of software supply chain risk. Under 60 seconds per SBOM,
            at any scale, with zero professional services required. That platform is{' '}
            <strong style={{color:'#1e293b'}}>DepGuard by Lexint</strong>.
          </p>
        </div>

        {/* I */}
        <SectionDivider num="I" title="The Open Source Risk Problem" />

        <p style={{fontSize:17,color:'#334155',lineHeight:1.85,marginBottom:24}}>
          Open source software has become the foundation of modern digital infrastructure. The 2024 Open Source
          Security and Risk Analysis Report found that <strong>96% of enterprise codebases contain open source
          components</strong>. This is not merely a trend. It is the baseline operating reality for every
          organization that ships software today.
        </p>

        <PullQuote source="Gartner + Sonatype State of the Software Supply Chain 2024">
          "45% of organizations will have experienced a software supply chain attack by 2025.
          Repository attacks increased 700% over three years. This is not a future risk &mdash; it is happening now."
        </PullQuote>

        <p style={{fontSize:17,color:'#334155',lineHeight:1.85,marginBottom:20}}>
          The consequences are severe and measurable. The SolarWinds breach cost businesses an average of
          11% of their annual revenue. The Log4j vulnerability exposed billions of systems and required years
          of patching effort across the industry. What makes supply chain attacks uniquely dangerous is precisely
          what makes open source powerful: the depth and invisibility of transitive dependencies.
        </p>

        <p style={{fontSize:17,color:'#334155',lineHeight:1.85,marginBottom:40}}>
          IEEE research found that <strong>systems using outdated dependencies are four times more likely
          to have security vulnerabilities</strong> than systems that are current. The implication is clear:
          knowing what is in your software, and knowing the health of every component, is not optional.
        </p>

        <div style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:16,padding:'32px 36px',marginBottom:48}}>
          <div style={{fontSize:12,fontWeight:700,color:'#94a3b8',letterSpacing:'.5px',textTransform:'uppercase',marginBottom:24}}>The Regulatory Imperative</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',gap:24}}>
            {[
              { flag: '\u{1F1EA}\u{1F1FA}', name: 'EU Cyber Resilience Act', detail: 'Mandatory SBOMs, vulnerability monitoring, due diligence on OSS. Penalties up to \u20AC15M or 2.5% of global annual revenue.' },
              { flag: '\u{1F1FA}\u{1F1F8}', name: 'US Executive Order 14028', detail: 'SBOM required for federal software supply chains. NIST frameworks mandated for critical infrastructure.' },
              { flag: '\u{1F310}',           name: 'ISO/IEC 27001:2022',     detail: 'Updated standard explicitly requires supply chain security controls and software dependency management.' },
            ].map(r => (
              <div key={r.name} style={{borderLeft:'3px solid #6366f1',paddingLeft:16}}>
                <div style={{fontSize:22,marginBottom:8}}>{r.flag}</div>
                <div style={{fontSize:14,fontWeight:700,color:'#1e293b',marginBottom:6}}>{r.name}</div>
                <div style={{fontSize:13,color:'#64748b',lineHeight:1.6}}>{r.detail}</div>
              </div>
            ))}
          </div>
        </div>

        {/* II */}
        <SectionDivider num="II" title="Why Point Solutions Fail at Scale" />

        <p style={{fontSize:17,color:'#334155',lineHeight:1.85,marginBottom:28}}>
          The standard enterprise response is to purchase or build a collection of specialized tools, each
          addressing one facet of the supply chain risk problem. This approach appears reasonable until it is
          tested against operational reality at enterprise scale.
        </p>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',gap:16,marginBottom:40}}>
          {[
            { icon: '\u{1F40C}', color: '#ef4444', title: 'The Speed Problem',         text: 'Some health monitoring solutions take 2\u20133 hours to process a list of packages. Others require months of professional services before delivering a single result. At 80,000 repositories, this is not a delay \u2014 it is an impossibility.' },
            { icon: '\u{1F9E9}', color: '#f59e0b', title: 'The Fragmentation Problem',  text: 'No single view of risk. Security teams toggle between 5+ dashboards. Correlation is manual. Decisions are made without the complete picture, and SBOM quality is nobody\'s problem until an auditor asks.' },
            { icon: '\u{1F4C4}', color: '#8b5cf6', title: 'The Compliance Problem',    text: 'No point tool produces EU CRA audit documentation. When regulators request evidence of due diligence, organizations assemble documentation manually: expensively, slowly, and imperfectly.' },
          ].map(p => (
            <div key={p.title} style={{background:'#fff',border:'1px solid #e2e8f0',borderTop:`3px solid ${p.color}`,borderRadius:14,padding:'28px 22px'}}>
              <div style={{fontSize:28,marginBottom:12}}>{p.icon}</div>
              <div style={{fontSize:15,fontWeight:700,color:'#1e293b',marginBottom:8}}>{p.title}</div>
              <div style={{fontSize:14,color:'#64748b',lineHeight:1.65}}>{p.text}</div>
            </div>
          ))}
        </div>

        <PullQuote source="Enterprise SBOM Benchmarking Report, 2024">
          "Organizations managing more than 1,000 repositories with point-tool approaches
          spend an average of 3.2 FTE-months per year on manual correlation and compliance
          documentation alone, before any remediation work begins."
        </PullQuote>

        <h3 style={{fontSize:20,fontWeight:700,color:'#1e293b',marginBottom:4,marginTop:48}}>Platform Comparison Matrix</h3>
        <p style={{fontSize:14,color:'#94a3b8',marginBottom:20}}>DepGuard vs. health-monitor tools, SCA tools, and custom-built stacks</p>
        <div style={{overflowX:'auto',marginBottom:16,borderRadius:14,border:'1px solid #e2e8f0',overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:14}}>
            <thead>
              <tr>
                <th style={{padding:'14px 18px',textAlign:'left',color:'#475569',fontWeight:600,background:'#f8fafc',borderBottom:'2px solid #e2e8f0'}}>Capability</th>
                <th style={{padding:'14px 18px',textAlign:'center',color:'#6366f1',fontWeight:800,background:'rgba(99,102,241,.06)',borderBottom:'2px solid #6366f1'}}>DepGuard</th>
                <th style={{padding:'14px 18px',textAlign:'center',color:'#64748b',fontWeight:600,background:'#f8fafc',borderBottom:'2px solid #e2e8f0'}}>Health Monitor</th>
                <th style={{padding:'14px 18px',textAlign:'center',color:'#64748b',fontWeight:600,background:'#f8fafc',borderBottom:'2px solid #e2e8f0'}}>SCA Tool</th>
                <th style={{padding:'14px 18px',textAlign:'center',color:'#64748b',fontWeight:600,background:'#f8fafc',borderBottom:'2px solid #e2e8f0'}}>DIY Stack</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row, i) => {
                const dg = cellVal(row.dg);
                const h  = cellVal(row.health);
                const s  = cellVal(row.sca);
                const d  = cellVal(row.diy);
                return (
                  <tr key={row.feature} style={{background: i % 2 === 0 ? '#fff' : '#fafafa'}}>
                    <td style={{padding:'11px 18px',color:'#334155',fontWeight:500,borderBottom:'1px solid #f1f5f9'}}>{row.feature}</td>
                    <td style={{padding:'11px 18px',textAlign:'center',color:dg.color,fontWeight:700,borderBottom:'1px solid #f1f5f9',background:'rgba(99,102,241,.03)'}}>{dg.label}</td>
                    <td style={{padding:'11px 18px',textAlign:'center',color:h.color, fontWeight:500,borderBottom:'1px solid #f1f5f9'}}>{h.label}</td>
                    <td style={{padding:'11px 18px',textAlign:'center',color:s.color, fontWeight:500,borderBottom:'1px solid #f1f5f9'}}>{s.label}</td>
                    <td style={{padding:'11px 18px',textAlign:'center',color:d.color, fontWeight:500,borderBottom:'1px solid #f1f5f9'}}>{d.label}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* III */}
        <SectionDivider num="III" title="Five Dimensions of SBOM Intelligence" />

        <p style={{fontSize:17,color:'#334155',lineHeight:1.85,marginBottom:40}}>
          DepGuard was designed from the ground up to analyze the complete risk profile of every dependency,
          not just one facet in isolation. Five dimensions, computed in parallel, from a single SBOM upload.
          Every dimension contributes to a unified composite risk score with full drill-down visibility.
        </p>

        <div style={{display:'flex',flexDirection:'column',gap:16,marginBottom:48}}>
          {PILLARS.map(p => (
            <div key={p.num} style={{
              display:'grid',gridTemplateColumns:'72px 1fr',gap:24,alignItems:'start',
              background:'#fff',border:'1px solid #e2e8f0',borderLeft:`4px solid ${p.color}`,
              borderRadius:'0 14px 14px 0',padding:'24px 28px',
            }}>
              <div style={{
                width:56,height:56,borderRadius:14,background:p.bg,
                display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,flexShrink:0,
              }}>{p.icon}</div>
              <div>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
                  <span style={{fontSize:11,fontWeight:800,color:p.color,letterSpacing:'.6px',textTransform:'uppercase'}}>{p.num}</span>
                  <span style={{fontSize:16,fontWeight:700,color:'#1e293b'}}>{p.name}</span>
                </div>
                <p style={{fontSize:15,color:'#475569',lineHeight:1.72,margin:0}}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{background:'linear-gradient(135deg, rgba(99,102,241,.06), rgba(139,92,246,.03))',border:'1px solid rgba(99,102,241,.15)',borderRadius:16,padding:'28px 32px',marginBottom:16}}>
          <strong style={{fontSize:15,color:'#1e293b'}}>Cross-Project Dependency Impact Graph: </strong>
          <span style={{fontSize:15,color:'#475569',lineHeight:1.7}}>
            An enterprise-only capability. When a package carries a critical vulnerability, organizations with
            tens of thousands of projects need to know their blast radius immediately. DepGuard&apos;s interactive
            graph maps every shared dependency to every project, so a single CVE alert instantly reveals
            which projects are affected, enabling prioritized, coordinated response.
          </span>
        </div>

        {/* IV */}
        <SectionDivider num="IV" title="How DepGuard Works: From Upload to Compliance" />

        <p style={{fontSize:17,color:'#334155',lineHeight:1.85,marginBottom:40}}>
          DepGuard eliminates the implementation overhead that makes most enterprise security tools impractical.
          No consulting engagement. No months of configuration. No application restarts between scans.
          From SBOM upload to actionable insight in under 60 seconds.
        </p>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))',gap:16,marginBottom:48}}>
          {WORKFLOW.map(step => (
            <div key={step.n} style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:14,padding:'28px 22px',position:'relative',overflow:'hidden'}}>
              <div style={{position:'absolute',top:12,right:16,fontSize:40,fontWeight:900,color:'rgba(99,102,241,.05)',lineHeight:1,userSelect:'none'}}>{step.n}</div>
              <div style={{fontSize:26,marginBottom:12}}>{step.icon}</div>
              <div style={{fontSize:15,fontWeight:700,color:'#1e293b',marginBottom:8}}>{step.title}</div>
              <div style={{fontSize:14,color:'#64748b',lineHeight:1.65}}>{step.desc}</div>
            </div>
          ))}
        </div>

        <div style={{background:'#1e293b',borderRadius:16,padding:'28px 32px',marginBottom:48}}>
          <div style={{fontSize:11,fontWeight:700,color:'#475569',letterSpacing:'.5px',textTransform:'uppercase',marginBottom:14}}>CI/CD Integration: GitHub Actions</div>
          <pre style={{margin:0,fontSize:13,color:'#a5b4fc',fontFamily:"'JetBrains Mono', 'Fira Code', monospace",lineHeight:1.7,overflowX:'auto',whiteSpace:'pre'}}>{`- name: DepGuard SBOM Security Gate
  uses: lexint/depguard-action@v1
  with:
    sbom: ./sbom.cyclonedx.json
    policy: high-severity-block
    fail-on: CRITICAL,HIGH
    cra-report: true      # generate EU CRA audit doc`}</pre>
        </div>

        {/* V */}
        <SectionDivider num="V" title="EU CRA Compliance: Built In, Not Bolted On" />

        <p style={{fontSize:17,color:'#334155',lineHeight:1.85,marginBottom:28}}>
          The European Cyber Resilience Act imposes binding obligations on every organization that places software
          products on the EU market. Non-compliance carries penalties of up to <strong>&euro;15 million</strong> or
          2.5% of global annual turnover. Unlike tools that require manual documentation assembly, DepGuard
          provides purpose-built support for every relevant CRA requirement, out of the box, from day one.
        </p>

        <div style={{overflowX:'auto',marginBottom:40,borderRadius:14,border:'1px solid #e2e8f0',overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:14}}>
            <thead>
              <tr>
                <th style={{padding:'14px 18px',textAlign:'left',color:'#475569',fontWeight:600,background:'#f8fafc',borderBottom:'2px solid #e2e8f0',whiteSpace:'nowrap'}}>Article</th>
                <th style={{padding:'14px 18px',textAlign:'left',color:'#475569',fontWeight:600,background:'#f8fafc',borderBottom:'2px solid #e2e8f0'}}>Legal Obligation</th>
                <th style={{padding:'14px 18px',textAlign:'left',color:'#6366f1',fontWeight:700,background:'rgba(99,102,241,.05)',borderBottom:'2px solid #6366f1'}}>DepGuard Coverage</th>
              </tr>
            </thead>
            <tbody>
              {CRA.map((row, i) => (
                <tr key={row.art} style={{background: i % 2 === 0 ? '#fff' : '#fafafa'}}>
                  <td style={{padding:'12px 18px',color:'#6366f1',fontWeight:700,borderBottom:'1px solid #f1f5f9',whiteSpace:'nowrap'}}>{row.art}</td>
                  <td style={{padding:'12px 18px',color:'#334155',borderBottom:'1px solid #f1f5f9'}}>{row.req}</td>
                  <td style={{padding:'12px 18px',color:'#047857',borderBottom:'1px solid #f1f5f9',background:'rgba(99,102,241,.02)'}}>{'✓ '}{row.dg}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <PullQuote source="DepGuard Compliance Team">
          "Organizations that generate EU CRA-compliant audit documentation from day one of DepGuard deployment
          have a decisive advantage when regulators and enterprise procurement teams request evidence of due
          diligence. The audit report is a one-click operation, not a multi-week project."
        </PullQuote>

        {/* VI */}
        <SectionDivider num="VI" title="Enterprise at Scale: Architecture & Performance" />

        <p style={{fontSize:17,color:'#334155',lineHeight:1.85,marginBottom:32}}>
          DepGuard was architected for organizations with tens of thousands of repositories, not hundreds.
          The performance profile makes continuous SBOM analysis practical at any enterprise scale.
        </p>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(155px, 1fr))',gap:14,marginBottom:48}}>
          {[
            { value: '< 60s',   label: 'per SBOM analysis',        sub: 'all 5 dimensions in parallel' },
            { value: '80K+',    label: 'repositories supported',    sub: 'concurrent real-time' },
            { value: '5 min',   label: 'to first insight',          sub: 'zero configuration required' },
            { value: '0',       label: 'professional services',     sub: 'fully self-service' },
            { value: '2',       label: 'SBOM formats',              sub: 'SPDX + CycloneDX' },
            { value: '2+',      label: 'CI/CD ecosystems',          sub: 'GitHub Actions + Azure DevOps' },
          ].map(s => (
            <div key={s.value} style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:14,padding:'24px 16px',textAlign:'center'}}>
              <div style={{fontSize:'clamp(24px,3.5vw,36px)',fontWeight:900,color:'#6366f1',letterSpacing:'-1px',lineHeight:1}}>{s.value}</div>
              <div style={{fontSize:12,color:'#1e293b',fontWeight:600,marginTop:8,lineHeight:1.3}}>{s.label}</div>
              <div style={{fontSize:11,color:'#94a3b8',marginTop:4}}>{s.sub}</div>
            </div>
          ))}
        </div>

        <h3 style={{fontSize:20,fontWeight:700,color:'#1e293b',marginBottom:16}}>The PRISM Foundation</h3>

        <p style={{fontSize:17,color:'#334155',lineHeight:1.85,marginBottom:20}}>
          DepGuard is built on PRISM, a Code Property Graph engine written in Go that delivers
          38ms cold starts with zero external runtime dependencies. PRISM parses source code once and
          serves all analysis clients from the same unified graph, eliminating the redundant parsing that
          makes traditional tool stacks slow.
        </p>

        <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:16,padding:'32px',marginBottom:48}}>
          <div style={{textAlign:'center',marginBottom:24,fontSize:11,fontWeight:700,color:'#94a3b8',letterSpacing:'.6px',textTransform:'uppercase'}}>Lexint Platform Architecture</div>
          <div style={{display:'flex',flexDirection:'column',gap:8,maxWidth:560,margin:'0 auto'}}>
            {[
              { label: 'DepGuard', sub: 'SBOM \u00B7 CVE \u00B7 License \u00B7 Health \u00B7 CRA \u00B7 AI', color: '#6366f1', bg: 'rgba(99,102,241,.07)' },
              { label: 'Vigil',    sub: 'SAST \u00B7 140+ rules \u00B7 7 languages \u00B7 PRL',              color: '#8b5cf6', bg: 'rgba(139,92,246,.07)' },
              { label: 'PRISM',   sub: 'Code Property Graph \u00B7 38ms \u00B7 Single Go binary',            color: '#06b6d4', bg: 'rgba(6,182,212,.07)' },
            ].map((layer, i, arr) => (
              <React.Fragment key={layer.label}>
                <div style={{background:layer.bg,border:`1px solid ${layer.color}30`,borderRadius:12,padding:'18px 28px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:16}}>
                  <span style={{fontWeight:800,color:layer.color,fontSize:16,flexShrink:0}}>{layer.label}</span>
                  <span style={{fontSize:13,color:'#64748b',textAlign:'right'}}>{layer.sub}</span>
                </div>
                {i < arr.length - 1 && <div style={{textAlign:'center',color:'#cbd5e1',fontSize:22,lineHeight:1}}>&#8595;</div>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* VII */}
        <SectionDivider num="VII" title="Business Impact & Return on Investment" />

        <p style={{fontSize:17,color:'#334155',lineHeight:1.85,marginBottom:40}}>
          The business case for unified SBOM intelligence is measured across three dimensions: risk reduction,
          operational efficiency, and regulatory readiness. All three contribute directly and measurably to
          the bottom line.
        </p>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(250px, 1fr))',gap:20,marginBottom:56}}>
          {ROI_CARDS.map(c => (
            <div key={c.title} style={{background:'#fff',border:'1px solid #e2e8f0',borderTop:`3px solid ${c.color}`,borderRadius:14,padding:'28px 24px'}}>
              <div style={{fontSize:28,marginBottom:12}}>{c.icon}</div>
              <div style={{fontSize:16,fontWeight:700,color:'#1e293b',marginBottom:16}}>{c.title}</div>
              <ul style={{margin:0,padding:0,listStyle:'none',display:'flex',flexDirection:'column',gap:8}}>
                {c.points.map(pt => (
                  <li key={pt} style={{display:'flex',gap:10,fontSize:14,color:'#475569',lineHeight:1.55}}>
                    <span style={{color:c.color,flexShrink:0,marginTop:1,fontWeight:700}}>&#10003;</span>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{
          background:'linear-gradient(135deg, #6366f1 0%, #8b5cf6 60%, #0891b2 100%)',
          borderRadius:20,padding:'64px 48px',textAlign:'center',
        }}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:'1px',textTransform:'uppercase',color:'rgba(255,255,255,.7)',marginBottom:16}}>Get Started</div>
          <h2 style={{fontSize:'clamp(26px,4.5vw,44px)',fontWeight:900,color:'#fff',letterSpacing:'-1px',marginBottom:20,lineHeight:1.1}}>
            See your complete supply chain risk<br/>in under 60 seconds
          </h2>
          <p style={{color:'rgba(255,255,255,.8)',fontSize:17,maxWidth:520,margin:'0 auto 40px',lineHeight:1.7}}>
            Upload a SPDX or CycloneDX SBOM and receive a full five-dimension risk analysis.
            No configuration. No professional services. No credit card.
          </p>
          <div style={{display:'flex',gap:14,justifyContent:'center',flexWrap:'wrap'}}>
            <Link to="/trial" style={{display:'inline-block',background:'#fff',color:'#6366f1',fontWeight:800,fontSize:16,padding:'16px 32px',borderRadius:12,textDecoration:'none',letterSpacing:'-.2px'}}>
              Start free trial &#8594;
            </Link>
            <button
              onClick={() => window.print()}
              style={{display:'inline-block',background:'rgba(255,255,255,.12)',border:'1px solid rgba(255,255,255,.3)',color:'#fff',fontWeight:600,fontSize:16,padding:'16px 32px',borderRadius:12,cursor:'pointer',letterSpacing:'-.2px'}}
            >
              Download PDF &#8595;
            </button>
          </div>
          <p style={{fontSize:13,color:'rgba(255,255,255,.5)',marginTop:24}}>
            For enterprise evaluations and pilots,{' '}
            <Link to="/contact" style={{color:'rgba(255,255,255,.7)',fontWeight:600,textDecoration:'none'}}>contact our team</Link>.
          </p>
        </div>

      </div>
    </div>
  );
}
